/**
 * @fileoverview Photos API Module - Handles all photo-related API requests
 * 
 * This module provides a centralized interface for interacting with the photo-related
 * endpoints of the Grow backend API. It handles photo uploads, metadata updates,
 * deletions, and turning and cropping a photo's picture (and putting the
 * original back).
 * 
 * All functions use the configured apiClient, which adds the Firebase token
 * and the X-Grow-Garden header (a shared garden's photos are its owner's)
 * and fills in photos under review (lib/underReview).
 *
 * Endpoints, a plant's (P = /photos/:plantObjId/:photoId):
 * - POST   /photos/add (multipart: image, plantObjId, dateTaken YYYY-MM-DD,
 *   stageId?, noteId?) → { success, photo: PlantPhoto }.
 * - PUT    P { dateTaken, stageId?, noteId? } → { photo }.
 * - PUT    P/plant { targetPlantObjId } → { success, photo }.
 * - PUT    P/public { hidden } → { success, photo_id, hidden_from_public }.
 * - PUT    P/file (multipart: image) → { success, photo }.
 * - GET    P/file[?size=card&v=] → the image bytes, through our own origin.
 * - PUT    P/image ImageEdit → { success, photo }; PUT P/image/reset.
 * - DELETE P.
 * A location's are the same under /locations/:id/photos/:photoId (photo 0
 * is the location's cover), and add with a POST to /locations/:id/photos.
 * A refusal's words come back in the body (lib/errorMessage refusalWords).
 *
 * Dates are sent as calendar days, never instants (asCalendarDay): see its
 * note.
 *
 * Who uses it: features/plant-list's photo dialogs (UploadPhotoDialog,
 * EditPhotoDialog, usePhotoActions), the bed dialog's cover
 * (EditLocationDialog) and the share picture-maker (shareFrames). Nothing is
 * cached here: a plant's photos arrive inside the plants list
 * (['plants']), which the dialogs update or invalidate after a change.
 *
 * @module photos
 */

import { apiClient } from './apiClient';
import { PlantPhoto } from '../types/photo';
import { DateTime } from '../utils/dateUtils';
import type { Subject } from './subject';
import type { ImageEdit } from '../lib/imageEdit';
import { t } from '../i18n/i18nSetup';
import { refusalWords } from '../lib/errorMessage';
import { trackEvent } from '@/shared/lib/analytics';
import { PhotoCopySkipped, isCopyAnswer, copyFields, oneAtATime, type CopyOptions } from '../lib/photoCopiesCore';

/**
 * The calendar day a Date stands for, as the server wants to read it.
 *
 * These dates are days, not moments — "the twelfth", not "the twelfth at
 * midnight somewhere". Sent as an instant, the server took its UTC calendar
 * day, which is a different day for half the world half the time: local
 * midnight on the twelfth is the eleventh in UTC anywhere east of Greenwich.
 * Sending the day itself takes the timezone out of the conversation.
 *
 * @param date - A JS Date standing for a local calendar day.
 * @returns That day as YYYY-MM-DD, which the API accepts as-is.
 */
const asCalendarDay = (date: Date): string =>
  DateTime.fromJSDate(date).toFormat('yyyy-LL-dd');

/**
 * Delete a photo for a plant
 * 
 * @async
 * @param {number} plantId - The ID of the plant
 * @param {number} photoId - The ID of the photo to delete
 * @returns {Promise<void>} Promise that resolves when the photo is deleted
 * @throws {Error} If the deletion fails or returns a non-200 status
 * 
 * @example
 * ```ts
 * try {
 *   await deletePhoto(123, 456);
 *   console.log('Photo deleted successfully');
 * } catch (error) {
 *   console.error('Failed to delete photo:', error);
 * }
 * ```
 */
export const deletePhoto = async (plantId: number, photoId: number): Promise<void> => {
  const response = await apiClient.delete(`/photos/${plantId}/${photoId}`);
  if (response.status !== 200) {
    throw new Error(t('plants.photo.api.deleteFailed'));
  }
};

/**
 * Upload a new photo for a plant
 * 
 * @async
 * @param {File} file - The photo file to upload
 * @param {number} plantId - The ID of the plant
 * @param {number} stageId - No longer asked: a photo carries no stage. Sent only when given.
 * @param {Date} dateTaken - The date the photo was taken
 * @param noteId - The note describing it, when one was written with it.
 * @param copy - The photo's Google Photos item id where it has one, and keepCopy on Undo (B61).
 * @returns {Promise<PlantPhoto>} Promise that resolves with the uploaded photo data
 * @throws {PhotoCopySkipped} When the server says the photo is an exact copy of one already added (nothing was stored).
 * @throws {Error} If the upload fails or returns a non-200 status
 * 
 * @example
 * ```ts
 * try {
 *   const photo = await uploadPhoto(
 *     fileObject,
 *     123,
 *     1,
 *     new Date()
 *   );
 *   console.log('Photo uploaded:', photo);
 * } catch (error) {
 *   console.error('Failed to upload photo:', error);
 * }
 * ```
 */
export const uploadPhoto = async (
  file: File,
  plantId: number,
  /** No longer asked (a photo carries no stage); sent only when given. */
  stageId: number | undefined,
  dateTaken: Date,
  /** Note describing this photo, so the picture can point at its words. */
  noteId?: number | null,
  copy?: CopyOptions,
): Promise<PlantPhoto> => {
  const formData = new FormData();
  formData.append('image', file);
  formData.append('plantObjId', plantId.toString());
  Object.entries(copyFields(copy)).forEach(([name, value]) => formData.append(name, value));
  // A photo carries no stage now (a plant's stage is its history); sent only if given.
  if (stageId !== undefined && stageId !== null) formData.append('stageId', stageId.toString());
  formData.append('dateTaken', asCalendarDay(dateTaken));
  // Sent only when there is one; the server ignores it unless the note belongs
  // to the same person and the same plant.
  if (noteId !== undefined && noteId !== null) {
    formData.append('noteId', String(noteId));
  }

  const response = await oneAtATime(() => apiClient.post('/photos/add', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  }));

  if (isCopyAnswer(response.data)) {
    throw new PhotoCopySkipped(() => uploadPhoto(file, plantId, stageId, dateTaken, noteId, { ...copy, keepCopy: true }));
  }
  if (!response.data.success) {
    throw new Error(refusalWords(response.data) ?? t('plants.photo.api.uploadFailed'));
  }

  trackEvent('photos_added', { count: 1, where: 'plant' });
  return response.data.photo;
};

/**
 * Update photo metadata
 * 
 * @async
 * @param {number} plantId - The ID of the plant
 * @param {number} photoId - The ID of the photo
 * @param {number} stageId - No longer asked; undefined leaves the photo's as it was.
 * @param {Date} dateTaken - The new date taken
 * @param noteId - The note it is about: a number links, null clears, undefined leaves alone.
 * @returns {Promise<PlantPhoto>} Promise that resolves with the updated photo data
 * @throws {Error} If the update fails or returns a non-200 status
 * 
 * @example
 * ```ts
 * try {
 *   const updatedPhoto = await updatePhotoMetadata(
 *     123,
 *     456,
 *     2,
 *     new Date()
 *   );
 *   console.log('Photo metadata updated:', updatedPhoto);
 * } catch (error) {
 *   console.error('Failed to update photo metadata:', error);
 * }
 * ```
 */
export const updatePhotoMetadata = async (
  plantId: number,
  photoId: number,
  /** No longer asked: the photo keeps whatever it had when none is sent. */
  stageId: number | undefined,
  dateTaken: Date,
  /**
   * The note this photo is about.
   *
   * Three meanings, deliberately: a number links it, null clears the link, and
   * undefined says nothing about notes at all and leaves whatever is there.
   */
  noteId?: number | null
): Promise<PlantPhoto> => {
  const response = await apiClient.put(`/photos/${plantId}/${photoId}`, {
    ...(stageId === undefined ? {} : { stageId }),
    dateTaken: asCalendarDay(dateTaken),
    ...(noteId === undefined ? {} : { noteId }),
  });

  if (response.status !== 200) {
    throw new Error(t('plants.photo.api.updateFailed'));
  }

  return response.data.photo;
};

/**
 * Move a photo onto a different plant.
 *
 * Photographing the wrong card is easy to do and, until this existed, only
 * undoable by deleting the picture. The note the photo carried is left behind
 * with the plant it was written about; the picture arrives without words.
 *
 * @async
 * @param {number} plantId - The plant the photo is on now.
 * @param {number} photoId - The photo to move.
 * @param {number} targetPlantId - The plant it should belong to.
 * @returns {Promise<PlantPhoto>} The photo as it now stands, with fresh URLs.
 * @throws {Error} If either plant is not the caller's, or the move fails.
 */
export const movePhotoToPlant = async (
  plantId: number,
  photoId: number,
  targetPlantId: number
): Promise<PlantPhoto> => {
  const response = await apiClient.put(`/photos/${plantId}/${photoId}/plant`, {
    targetPlantObjId: targetPlantId,
  });

  if (!response.data?.success) {
    throw new Error(refusalWords(response.data) ?? t('plants.photo.api.moveFailed'));
  }

  return response.data.photo;
};

/**
 * Hide a plant's photo from the gardener's public pages, or show it again.
 *
 * A Public plant shows its photos on the public pages; a hidden one never
 * appears there. A share link that picked it still shows it.
 *
 * @async
 * @param {number} plantId - The plant the photo is on.
 * @param {number} photoId - The photo.
 * @param {boolean} hidden - True to hide it, false to show it again.
 * @returns {Promise<boolean>} Whether the photo is hidden now.
 * @throws {Error} If the photo is not the caller's or the change fails.
 */
export const setPhotoHiddenFromPublic = async (
  plantId: number,
  photoId: number,
  hidden: boolean
): Promise<boolean> => {
  const response = await apiClient.put(`/photos/${plantId}/${photoId}/public`, { hidden });

  if (!response.data?.success) {
    throw new Error(refusalWords(response.data) ?? t('plants.photo.api.hideFailed'));
  }

  return Boolean(response.data.hidden_from_public);
};

/**
 * Replace the picture without replacing the photo.
 *
 * Keeps the photo_id, and with it the date, the stage, the note and the place
 * in the carousel. Uploading a new photo and deleting the old one would lose
 * all four.
 *
 * @async
 * @param {number} plantId - The plant the photo belongs to.
 * @param {number} photoId - The photo whose picture is changing.
 * @param {File} file - The new image.
 * @returns {Promise<PlantPhoto>} The photo with its new URLs.
 * @throws {Error} If the upload fails.
 */
export const replacePhotoFile = async (
  plantId: number,
  photoId: number,
  file: File
): Promise<PlantPhoto> => {
  const formData = new FormData();
  formData.append('image', file);

  const response = await apiClient.put(`/photos/${plantId}/${photoId}/file`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

  if (!response.data?.success) {
    throw new Error(refusalWords(response.data) ?? t('plants.photo.api.replaceFailed'));
  }

  return response.data.photo;
};

/**
 * Fetch a photo's full-size bytes from our own origin.
 *
 * The stored files sit behind signed URLs on a bucket that sends no CORS
 * headers, so the browser will display them and not read them. Downloading
 * means reading them, which means asking the API for the bytes instead.
 *
 * @async
 * @param {number} plantId - The plant the photo belongs to.
 * @param {number} photoId - The photo to fetch.
 * @returns {Promise<Blob>} The image, full size.
 */
export const fetchPhotoBlob = async (plantId: number, photoId: number): Promise<Blob> => {
  const response = await apiClient.get<Blob>(`/photos/${plantId}/${photoId}/file`, {
    responseType: 'blob',
  });
  return response.data;
};

/*
 * The same photo actions for either subject. A plant's go through the plant
 * routes above; a location's through /locations/:id/photos, which answer in
 * the same shape (a PlantPhoto, without a stage).
 */

/** A location's photo routes. */
const locationPhotos = (id: number): string => `/locations/${id}/photos`;

/**
 * Adds a photo to a plant or a location; a plant's photo carries no stage unless an old caller sends one.
 *
 * @param subject - The plant or location.
 * @param file - The picture, already shrunk.
 * @param dateTaken - The day it was taken.
 * @param noteId - The note written with it, if any.
 * @param stageId - Only from an old caller.
 * @param copy - The photo's Google Photos item id where it has one, and keepCopy on Undo (B61).
 * @returns The photo as stored.
 * @throws PhotoCopySkipped when the server says the photo is an exact copy of one already added.
 */
export async function uploadPhotoTo(
  subject: Subject, file: File, dateTaken: Date, noteId?: number | null, stageId?: number, copy?: CopyOptions,
): Promise<PlantPhoto> {
  if (subject.kind === 'plant') return uploadPhoto(file, subject.id, stageId, dateTaken, noteId, copy);
  const formData = new FormData();
  formData.append('image', file);
  formData.append('dateTaken', asCalendarDay(dateTaken));
  if (noteId !== undefined && noteId !== null) formData.append('noteId', String(noteId));
  Object.entries(copyFields(copy)).forEach(([name, value]) => formData.append(name, value));
  const response = await oneAtATime(() => apiClient.post(locationPhotos(subject.id), formData, { headers: { 'Content-Type': 'multipart/form-data' } }));
  if (isCopyAnswer(response.data)) {
    throw new PhotoCopySkipped(() => uploadPhotoTo(subject, file, dateTaken, noteId, stageId, { ...copy, keepCopy: true }));
  }
  trackEvent('photos_added', { count: 1, where: 'bed' });
  return response.data.photo;
}

/**
 * Changes a photo's date and words, a plant's or a location's.
 *
 * @param subject - The plant or location.
 * @param photoId - The photo.
 * @param dateTaken - The new day.
 * @param noteId - A number links, null clears, undefined leaves alone.
 * @param stageId - Only from an old caller.
 * @returns The photo as stored.
 */
export async function updatePhotoFor(
  subject: Subject, photoId: number, dateTaken: Date, noteId?: number | null, stageId?: number,
): Promise<PlantPhoto> {
  if (subject.kind === 'plant') return updatePhotoMetadata(subject.id, photoId, stageId, dateTaken, noteId);
  const response = await apiClient.put(`${locationPhotos(subject.id)}/${photoId}`, {
    dateTaken: asCalendarDay(dateTaken),
    ...(noteId === undefined ? {} : { noteId }),
  });
  return response.data.photo;
}

/**
 * Replaces a photo's picture, keeping its date and words.
 *
 * @param subject - The plant or location.
 * @param photoId - The photo.
 * @param file - The new picture.
 * @returns The photo with its new addresses.
 */
export async function replacePhotoFileFor(subject: Subject, photoId: number, file: File): Promise<PlantPhoto> {
  if (subject.kind === 'plant') return replacePhotoFile(subject.id, photoId, file);
  const formData = new FormData();
  formData.append('image', file);
  const response = await apiClient.put(`${locationPhotos(subject.id)}/${photoId}/file`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  return response.data.photo;
}

/**
 * Deletes a photo, a plant's or a location's.
 *
 * @param subject - The plant or location.
 * @param photoId - The photo.
 */
export async function deletePhotoFor(subject: Subject, photoId: number): Promise<void> {
  if (subject.kind === 'plant') return deletePhoto(subject.id, photoId);
  await apiClient.delete(`${locationPhotos(subject.id)}/${photoId}`);
}

/**
 * A photo's picture, full size, through our own origin.
 *
 * @param subject - The plant or location.
 * @param photoId - The photo.
 */
export async function fetchPhotoBlobFor(subject: Subject, photoId: number): Promise<Blob> {
  if (subject.kind === 'plant') return fetchPhotoBlob(subject.id, photoId);
  const response = await apiClient.get<Blob>(`${locationPhotos(subject.id)}/${photoId}/file`, { responseType: 'blob' });
  return response.data;
}

/**
 * How many times each photo's picture has been turned, cropped or reset in
 * this visit, by subject and photo ("plant:12:40").
 *
 * A photo's file address (photoFilePath) is the photo's, not its picture's,
 * so after an edit it is the same address with a different picture behind
 * it. The share's picture-maker keeps what it fetched by address for the
 * visit (shareFrames), so the count goes on the address and an edited photo
 * is fetched afresh. The server answers by ETag, so the browser's own cache
 * asks again on its own.
 */
const pictureVersions = new Map<string, number>();

/**
 * The key a photo's picture count is kept under.
 *
 * @param subject - The plant or location.
 * @param photoId - The photo (0 for a location's cover).
 */
const pictureKey = (subject: Subject, photoId: number): string => `${subject.kind}:${subject.id}:${photoId}`;

/**
 * The path a share's picture-maker fetches a photo from, relative to the API.
 *
 * @param subject - The plant or location.
 * @param photoId - The photo.
 * @param size - 'card' for the smaller rendition; omitted for full size.
 */
export function photoFilePath(subject: Subject, photoId: number, size?: 'card'): string {
  const base = subject.kind === 'plant' ? `/photos/${subject.id}/${photoId}/file` : `${locationPhotos(subject.id)}/${photoId}/file`;
  const version = pictureVersions.get(pictureKey(subject, photoId));
  const query = [size ? `size=${size}` : null, version ? `v=${version}` : null].filter(Boolean).join('&');
  return query ? `${base}?${query}` : base;
}

/** What turning, cropping or resetting a photo answers: its picture's new name and links. */
export type EditedPicture = Pick<PlantPhoto, 'photo_id' | 'filename' | 'signed_photo_url' | 'cardUrl' | 'thumbUrl'> & Partial<PlantPhoto>;

/**
 * The address a photo's picture is edited at: a plant's, or a location's
 * (photo 0 is the location's cover).
 *
 * @param subject - The plant or location.
 * @param photoId - The photo.
 */
const pictureEditPath = (subject: Subject, photoId: number): string =>
  subject.kind === 'plant' ? `/photos/${subject.id}/${photoId}/image` : `${locationPhotos(subject.id)}/${photoId}/image`;

/**
 * Turns and crops a photo's picture, a plant's or a location's (photo 0 is a
 * location's cover). The server keeps the untouched original beside it.
 *
 * @param subject - The plant or location.
 * @param photoId - The photo.
 * @param edit - The turn and crop (shared/lib/imageEdit).
 * @returns The picture's new name and links, to merge over the photo.
 * @throws When the server refuses (its words) or the request fails.
 */
export async function editPhotoImageFor(subject: Subject, photoId: number, edit: ImageEdit): Promise<EditedPicture> {
  const response = await apiClient.put(pictureEditPath(subject, photoId), edit);
  if (!response.data?.success) {
    throw new Error(refusalWords(response.data) ?? t('plants.photo.edit.failed'));
  }
  pictureVersions.set(pictureKey(subject, photoId), (pictureVersions.get(pictureKey(subject, photoId)) ?? 0) + 1);
  return response.data.photo;
}

/**
 * Puts a photo's picture back as it was uploaded, undoing every turn and crop.
 *
 * @param subject - The plant or location.
 * @param photoId - The photo (0 for a location's cover).
 * @returns The original's name and links, to merge over the photo.
 * @throws When the server refuses (its words) or the request fails.
 */
export async function resetPhotoImageFor(subject: Subject, photoId: number): Promise<EditedPicture> {
  const response = await apiClient.put(`${pictureEditPath(subject, photoId)}/reset`);
  if (!response.data?.success) {
    throw new Error(refusalWords(response.data) ?? t('plants.photo.edit.resetFailed'));
  }
  pictureVersions.set(pictureKey(subject, photoId), (pictureVersions.get(pictureKey(subject, photoId)) ?? 0) + 1);
  return response.data.photo;
}
